import React, { FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { PredefinedMilisecondsTypeEnum } from '../../lib/enums';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getDefaultSelectableTradingPLanFromState } from '../../lib/store/crm/helpers';
import { CRMCall } from '../../lib/store/crm/types';
import { calculateTimeRemaining, formatTimeRemaining } from '../../lib/util/DateTimeHelpers';
import { isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import IconClockLight from '../../assets/img/clock-light.svg';
import { useIsMobile } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';
import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';

import TournamentEndingInfoModal from './TournamentEndingInfoModal/TournamentEndingInfoModal';

import styles from './TournamentEndTimer.module.scss';

type Props = ReduxProps & {};

const TournamentEndTimer: FC<Props> = (props: Props): JSX.Element => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { pathname } = location;
  const { tradingPlan, isTradingPlanReady } = props;
  const { plans_accounts_deactivation: endDate } = tradingPlan;

  const [isDisplayModal, setIsDisplayModal] = useState(false);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [hasNoRemainingTime, setHasNoRemainingTime] = useState(true); // we will display nothing until the calculation is complete that's why default is TRUE

  const componentDisplayPaths = [
    UI_ROUTES.overview,
    UI_ROUTES.discover,
    UI_ROUTES.myAccount,
    UI_ROUTES.myPosition,
    UI_ROUTES.tradeHistory,
  ];

  useEffect(() => {
    if (!isTradingPlanReady) {
      return;
    }
    calculateTimeRemaining(endDate, setHasNoRemainingTime, setDays, setHours, setMinutes, setSeconds); // Initial calculation
    const intervalId = setInterval(() => {
      calculateTimeRemaining(endDate, setHasNoRemainingTime, setDays, setHours, setMinutes, setSeconds);
    }, PredefinedMilisecondsTypeEnum.oneSecond);

    return () => clearInterval(intervalId); // eslint-disable-line consistent-return
  }, [endDate, isTradingPlanReady]);

  if (hasNoRemainingTime || !componentDisplayPaths.includes(pathname)) {
    return <></>;
  }

  return (
    <div className={classNames(styles.wrapper, { [styles.wrapperMob]: isMobile })}>
      <div className={styles.desc}>
        {lang.commonTournamentEndingAt()}
        <img alt="" src={IconClockLight} className={styles.clock} />
        {formatTimeRemaining(days, hours, minutes, seconds)}
      </div>
      <Button
        fullWidth={isMobile}
        variant={ButtonTypesEnum.Tertiary}
        className={styles.learnMoreButton}
        onClick={() => setIsDisplayModal(true)}
      >
        {lang.commonLearnMore()}
      </Button>
      {isDisplayModal && <TournamentEndingInfoModal setIsDisplayModal={setIsDisplayModal} />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  tradingPlan: getDefaultSelectableTradingPLanFromState(state.crm),
  isTradingPlanReady: isCallStatusReady(CRMCall.searchTradingPlans, state.crm.statusByCall),
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(TournamentEndTimer);
